import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Film, Clock, MapPin, Ticket as TicketIcon, ChevronRight, Hand, Sparkles, CalendarClock } from "lucide-react";
import { eventType } from "@/lib/constants";
import CinemaBrand from "@/components/CinemaBrand";
import IdleGuard from "@/components/IdleGuard";

const TABS = [
  { v: "aktuell", l: "Aktuell", icon: TicketIcon },
  { v: "neu", l: "Neu", icon: Sparkles },
  { v: "demnaechst", l: "Demnächst", icon: CalendarClock },
];

export default function Kiosk() {
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState([]);
  const [tab, setTab] = useState("aktuell");
  const [now, setNow] = useState(new Date());
  const [carouselIdx, setCarouselIdx] = useState(0);
  const nav = useNavigate();
  const rootRef = useRef(null);

  const reload = () => {
    api.get("/public/program").then((r) => setRows(r.data || [])).catch(() => {});
    api.get("/public/preview-movies").then((r) => setPreview(r.data || [])).catch(() => {});
  };
  useEffect(() => { reload(); }, []);

  useEffect(() => {
    const trigger = () => {
      document.documentElement.requestFullscreen?.().catch(() => {});
      window.removeEventListener("touchstart", trigger);
      window.removeEventListener("click", trigger);
    };
    window.addEventListener("touchstart", trigger, { once: true, passive: true });
    window.addEventListener("click", trigger, { once: true });
    return () => {
      window.removeEventListener("touchstart", trigger);
      window.removeEventListener("click", trigger);
    };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const heroPosters = useMemo(() => {
    const set = new Set();
    for (const s of rows) {
      const p = s.movie?.backdrop_url || s.movie?.poster_url;
      if (p) set.add(p);
    }
    for (const m of preview) {
      const p = m.backdrop_url || m.poster_url;
      if (p) set.add(p);
    }
    return Array.from(set);
  }, [rows, preview]);
  useEffect(() => {
    if (heroPosters.length <= 1) return;
    const t = setInterval(() => setCarouselIdx((i) => (i + 1) % heroPosters.length), 6000);
    return () => clearInterval(t);
  }, [heroPosters.length]);

  const goHomeReset = () => { window.scrollTo({ top: 0, behavior: "smooth" }); reload(); };

  const grouped = useMemo(() => {
    const m = {};
    for (const s of rows) (m[s.date] = m[s.date] || []).push(s);
    return m;
  }, [rows]);
  const dates = useMemo(() => Object.keys(grouped).sort(), [grouped]);

  const newMovies = useMemo(() => preview.filter((m) => m.status === "neu"), [preview]);
  const comingSoon = useMemo(() => preview.filter((m) => m.status === "demnaechst"), [preview]);

  const counts = { aktuell: rows.length, neu: newMovies.length, demnaechst: comingSoon.length };

  return (
    <div ref={rootRef} className="min-h-screen w-full bg-black text-[#F5F0E6] relative">
      <IdleGuard idleMs={60_000} graceMs={20_000} onExpire={goHomeReset} testId="kiosk-idle-guard" />

      {/* HERO */}
      <section className="relative overflow-hidden border-b-2 border-[#D4AF37]/60 min-h-[75vh] flex flex-col">
        {heroPosters.map((p, i) => (
          <img key={p} src={p} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }}
            className={`absolute inset-0 w-full h-full object-cover transitions-safe duration-[1500ms] ${i === carouselIdx ? "opacity-30" : "opacity-0"}`} />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-[#3B0C10]/30 via-black/70 to-black" />
        <div className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none"
          style={{ backgroundImage: "radial-gradient(#D4AF37 1px, transparent 1px)", backgroundSize: "3px 3px" }} />

        <div className="absolute inset-x-0 top-3 flex justify-between px-6 pointer-events-none z-10">
          {Array.from({ length: 24 }).map((_, i) => (
            <span key={i} className="w-2.5 h-2.5 rounded-full bg-[#D4AF37] shadow-[0_0_12px_rgba(212,175,55,0.9)] animate-bulb" style={{ animationDelay: `${(i % 4) * 220}ms` }} />
          ))}
        </div>
        <div className="absolute inset-x-0 bottom-3 flex justify-between px-6 pointer-events-none z-10">
          {Array.from({ length: 24 }).map((_, i) => (
            <span key={i} className="w-2.5 h-2.5 rounded-full bg-[#D4AF37] shadow-[0_0_12px_rgba(212,175,55,0.9)] animate-bulb" style={{ animationDelay: `${((i + 2) % 4) * 220}ms` }} />
          ))}
        </div>

        <div className="absolute top-8 right-10 text-right z-20">
          <div className="font-mono text-3xl md:text-5xl tabular-nums leading-none">{now.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#A8A398] mt-2">{now.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" })}</div>
        </div>

        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-8 text-center py-16">
          <div className="scale-125 md:scale-150 origin-center">
            <CinemaBrand size="xl" stacked />
          </div>
          <div className="mt-12 inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]/90">
            <span className="w-10 h-px bg-[#D4AF37]/60" /> Ticketautomat <span className="w-10 h-px bg-[#D4AF37]/60" />
          </div>
          <div className="font-heading text-4xl md:text-6xl mt-4 leading-none">Bitte Film wählen</div>
          <div className="mt-3 text-[#A8A398] italic">Tippen zum Auswählen — More Than Watching Movies.</div>
          <div className="mt-10 inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] animate-bounce-slow">
            <Hand size={14} /> Nach unten wischen für alle Filme
          </div>
        </div>
      </section>

      {/* KATEGORIE-TABS */}
      <div className="sticky top-0 z-30 bg-black/95 backdrop-blur border-b border-[#1A1A1A] px-6 md:px-10 py-4">
        <div className="flex gap-2 md:gap-3 justify-center flex-wrap" data-testid="kiosk-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.v;
            const cnt = counts[t.v];
            return (
              <button key={t.v} onClick={() => setTab(t.v)} data-testid={`kiosk-tab-${t.v}`}
                className={`flex items-center gap-2 px-5 md:px-7 py-3 rounded-full border transition-all uppercase tracking-[0.3em] text-[11px] ${active ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2A2A2A] text-[#A8A398] hover:border-[#D4AF37]/60 hover:text-[#F5F0E6]"}`}>
                <Icon size={14} />
                {t.l}
                {cnt > 0 && (
                  <span className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-mono ${active ? "bg-black/25 text-black" : "bg-[#D4AF37]/15 text-[#D4AF37]"}`}>{cnt}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* CONTENT */}
      <main className="p-6 md:p-10 space-y-14 pb-32">
        {tab === "aktuell" && (
          <>
            {dates.length === 0 ? (
              <EmptyState icon={<Film size={48} />} title="Zurzeit keine Vorstellungen" />
            ) : (
              dates.map((date) => (
                <DateBlock key={date} iso={date} shows={grouped[date]} onPick={(s) => nav(`/kiosk/ticket/${s.id}`)} />
              ))
            )}
          </>
        )}
        {tab === "neu" && (
          newMovies.length === 0
            ? <EmptyState icon={<Sparkles size={48} />} title="Keine neuen Filme angekündigt" hint={'Sobald ein Film als "Neu" markiert wird, erscheint er hier.'} />
            : <PreviewGrid movies={newMovies} tone="neu" />
        )}
        {tab === "demnaechst" && (
          comingSoon.length === 0
            ? <EmptyState icon={<CalendarClock size={48} />} title="Nichts Neues in der Pipeline" hint={'Setze im Admin den Status auf "Demnächst".'} />
            : <PreviewGrid movies={comingSoon} tone="demnaechst" />
        )}
      </main>

      <div className="fixed inset-x-0 bottom-0 border-t border-[#1A1A1A] bg-black/85 backdrop-blur px-8 py-4 flex items-center justify-between z-30">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#575148]">THE MOVIE HOUSE · Ticketautomat</div>
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#575148]">Zum Auswählen einen Film antippen</div>
      </div>

      <style>{`
        @keyframes bulb { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
        .animate-bulb { animation: bulb 1.8s ease-in-out infinite; }
        @keyframes bounceSlow { 0%,100% { transform: translateY(0); } 50% { transform: translateY(6px); } }
        .animate-bounce-slow { animation: bounceSlow 2s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

function EmptyState({ icon, title, hint }) {
  return (
    <div className="text-center py-24 text-[#A8A398]">
      <div className="text-[#D4AF37]/50 flex justify-center mb-3">{icon}</div>
      <div className="font-heading text-3xl">{title}</div>
      {hint && <div className="text-sm mt-2 text-[#575148]">{hint}</div>}
    </div>
  );
}

function PreviewGrid({ movies, tone }) {
  const chip = tone === "neu"
    ? { label: "NEU", cls: "bg-[#7A1921] text-white" }
    : { label: "DEMNÄCHST", cls: "bg-[#D4AF37] text-black" };
  return (
    <section>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={`kiosk-preview-${tone}`}>
        {movies.map((m) => (
          <div key={m.id} className="group relative rounded-lg overflow-hidden border border-[#1A1A1A] bg-[#0A0A0A]"
            data-testid={`kiosk-preview-movie-${m.id}`}>
            <div className="aspect-[3/4] bg-[#111] relative">
              {m.poster_url ? (
                <img src={m.poster_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#575148]"><Film size={40} /></div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute top-3 left-3 flex flex-col gap-2">
                <span className={`text-[9px] uppercase tracking-[0.4em] px-2.5 py-1 rounded-full ${chip.cls}`}>{chip.label}</span>
                {(m.age_rating || m.rating) && (
                  <span className="self-start text-[10px] font-mono border border-[#D4AF37]/70 text-[#D4AF37] bg-black/60 backdrop-blur px-2 py-0.5 rounded">
                    {String(m.age_rating || m.rating).replace(/^FSK\s*/i, "FSK ")}
                  </span>
                )}
              </div>
              {m.release_date && tone === "neu" && (
                <div className="absolute top-3 right-3 text-[9px] uppercase tracking-[0.32em] px-2 py-1 rounded bg-black/70 text-[#D4AF37] backdrop-blur">
                  ab {new Date(m.release_date + "T00:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit." })}
                </div>
              )}
              <div className="absolute inset-x-3 bottom-3">
                <div className="font-heading text-2xl leading-tight text-[#F5F0E6] line-clamp-2">{m.title_de || m.title_original}</div>
                <div className="text-xs text-[#A8A398] truncate mt-1">{m.genre}{m.duration_min ? ` · ${m.duration_min} Min` : ""}</div>
              </div>
            </div>
            {(m.synopsis || m.description) && (
              <div className="px-4 py-3 text-xs text-[#A8A398] line-clamp-3 min-h-[62px]">
                {m.synopsis || m.description}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function DateBlock({ iso, shows, onPick }) {
  const d = new Date(iso + "T00:00:00");
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  const label = diff === 0 ? "Heute" : diff === 1 ? "Morgen" : diff === 2 ? "Übermorgen" : null;
  return (
    <section>
      <div className="flex items-end gap-6 mb-5">
        <div className="text-center">
          <div className="font-heading text-6xl md:text-7xl text-[#D4AF37] leading-none">{d.getDate().toString().padStart(2, "0")}</div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mt-1">{d.toLocaleDateString("de-DE", { month: "short" }).replace(".", "")}</div>
        </div>
        <div className="flex-1 border-l-2 border-[#D4AF37]/40 pl-6">
          <div className="flex items-center gap-3 flex-wrap">
            {label && <span className="text-[10px] uppercase tracking-[0.4em] px-3 py-1 rounded-full border border-[#D4AF37] text-[#D4AF37]">{label}</span>}
            <span className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398]">{d.toLocaleDateString("de-DE", { weekday: "long" })}</span>
          </div>
          <div className="mt-1 font-heading text-3xl">{d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}</div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {shows.map((s) => <BigCard key={s.id} s={s} onPick={() => onPick(s)} />)}
      </div>
    </section>
  );
}

function BigCard({ s, onPick }) {
  const m = s.movie;
  const ev = eventType(s.event_type);
  const isEvent = s.event_type && s.event_type !== "regular";
  const age = m?.age_rating || m?.rating;
  return (
    <button data-testid={`kiosk-show-${s.id}`} onClick={onPick}
      className="group relative rounded-lg overflow-hidden border border-[#1A1A1A] hover:border-[#D4AF37] bg-[#0A0A0A] text-left active:scale-[0.98] transition">
      <div className="aspect-[3/4] bg-[#111] relative">
        {m?.poster_url ? (
          <img src={m.poster_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#575148]"><Film size={40} /></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {isEvent && (
            <span className="text-[9px] uppercase tracking-[0.32em] px-2 py-0.5 rounded-full border bg-black/60 backdrop-blur" style={{ color: ev.color, borderColor: ev.color + "80" }}>
              {ev.icon} {ev.l}
            </span>
          )}
          {age && (
            <span className="self-start text-[10px] font-mono border border-[#D4AF37]/70 text-[#D4AF37] bg-black/60 backdrop-blur px-2 py-0.5 rounded">{String(age).replace(/^FSK\s*/i, "FSK ")}</span>
          )}
        </div>
        <div className="absolute inset-x-3 bottom-3">
          <div className="font-heading text-2xl leading-tight text-[#F5F0E6] line-clamp-2">{m?.title_de || m?.title_original}</div>
          <div className="text-xs text-[#A8A398] truncate mt-1">{m?.genre}{m?.duration_min ? ` · ${m.duration_min} Min` : ""}</div>
        </div>
      </div>
      <div className="flex items-center justify-between px-4 py-3 bg-black">
        <div className="flex items-center gap-3 text-sm">
          <span className="flex items-center gap-1.5"><Clock size={14} className="text-[#D4AF37]" /><span className="font-mono">{s.start_time}</span></span>
          <span className="w-1 h-1 rounded-full bg-[#D4AF37]/60" />
          <span className="flex items-center gap-1.5 text-[#A8A398]"><MapPin size={12} />{s.hall}</span>
        </div>
        <div className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.3em] text-[#D4AF37]">
          <TicketIcon size={12} /> Ticket <ChevronRight size={14} />
        </div>
      </div>
    </button>
  );
}
